import React, { useState } from "react";
import {
  Box, Button, TextField, Typography, Paper, IconButton,
  InputAdornment, Checkbox, FormControlLabel,
  Dialog, DialogContent, CircularProgress, Snackbar, Alert,
} from "@mui/material";
import { keyframes } from "@emotion/react";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import { loginUser, registerUser, resendVerification } from "../api/authApi";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

import chatgptAiImg from "../assets/images/chatgpt+ai.webp";
import getStartedImg from "../assets/images/get-started.webp";
import logoImg from "../assets/images/talktokam.webp";

const spin360 = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const SpinningLogo = ({ size = 20 }) => (
  <Box sx={{
    width: size, height: size, borderRadius: '50%', overflow: 'hidden',
    animation: `${spin360} 0.8s linear infinite`,
    border: '2px solid rgba(255,255,255,0.8)',
    flexShrink: 0,
    opacity: 1,
  }}>
    <Box component="img" src={logoImg} alt=""
      sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
  </Box>
);

const contentFeatures = [
  { icon: <AutoAwesomeIcon />, text: "AI-powered chatbots in minutes" },
  { icon: <SpeedIcon />, text: "No coding required" },
  { icon: <SupportAgentIcon />, text: "Live chat handoff" },
  { icon: <SecurityIcon />, text: "Enterprise-grade security" },
];

const AuthForm = () => {
  const [isLogin, setIsLogin] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [requires2FA, setRequires2FA] = useState(false);
  const [totpToken, setTotpToken] = useState("");
  const [pendingPayload, setPendingPayload] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitting2FA, setSubmitting2FA] = useState(false);
  const [needsVerification, setNeedsVerification] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMsg, setSnackbarMsg] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [resending, setResending] = useState(false);
  const [resendDone, setResendDone] = useState(false);
  const [formData, setFormData] = useState({
    fullName: "", email: "", password: "", phone: "",
    website: "", termsAgreed: false,
  });

  const navigate = useNavigate();
  const { login } = useAuth();

  const CHATBOT_APP_URL = process.env.REACT_APP_CHATBOT_URL || "http://localhost:3000";
  const redirectAfter = new URLSearchParams(window.location.search).get("redirect") || "";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  const toggleMode = () => {
    setIsLogin((prev) => !prev);
    setError("");
    setFormData({ fullName: "", email: "", password: "", phone: "", website: "", termsAgreed: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);

    const payload = { email: formData.email, password: formData.password };

    if (!isLogin) {
      if (!formData.termsAgreed) { setError("You must agree to Terms & Conditions."); setSubmitting(false); return; }
      if (!formData.phone || !/^\+[1-9]\d{6,14}$/.test(formData.phone)) {
        setError("Please enter a valid phone number with country code.");
        setSubmitting(false);
        return;
      }
      Object.assign(payload, {
        fullName: formData.fullName,
        website: formData.website,
        phone: formData.phone,
        termsAccepted: formData.termsAgreed,
      });
    }

    try {
      const response = isLogin ? await loginUser(payload) : await registerUser(payload);

      if (response.requires2FA) {
        setPendingPayload(payload);
        setRequires2FA(true);
        setSubmitting(false);
        return;
      }

      if (isLogin) {
        // Login → redirect to app
        localStorage.setItem("userId", response.user._id);
        login(response);

        const token = response.token;
        const user = response.user;
        const plan = user.plan || '';
        const params = new URLSearchParams({
          token,
          userId: user._id,
          role: user.role || '',
          name: user.fullName || '',
          email: user.email || '',
          plan,
        });
        if (redirectAfter) params.set("redirect", redirectAfter);
        window.location.href = `${CHATBOT_APP_URL}/auth/callback?${params.toString()}`;
        return;
      }

      // Registration → send verification email
      setSubmitting(false);
      setSnackbarMsg(response.verificationSent
        ? "Verification email sent! Check your inbox (and spam)."
        : "Account created! But could not send verification email. Try requesting a new one after login.");
      setSnackbarSeverity(response.verificationSent ? "success" : "warning");
      setSnackbarOpen(true);
      setTimeout(() => navigate("/login"), 3000);
      return;
    } catch (err) {
      if (err?.needsVerification) {
        setNeedsVerification(err);
        setError("");
      } else {
        const msg = typeof err === "string" ? err : err?.message || "An error occurred";
          if (msg === "Email already registered") {
            setSnackbarMsg("This email is already registered. Please log in or use 'Forgot Password'.");
            setSnackbarSeverity("info");
        } else {
          setSnackbarMsg(msg);
          setSnackbarSeverity("error");
        }
        setSnackbarOpen(true);
        setError("");
      }
      setSubmitting(false);
    }
  };

  const handle2FASubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting2FA(true);
    try {
      const response = await loginUser({ ...pendingPayload, totpToken });
      localStorage.setItem("userId", response.user._id);
      login(response);
      const token = response.token;
      const user = response.user;
      const plan = user.plan || '';
      const params = new URLSearchParams({
        token,
        userId: user._id,
        role: user.role || '',
        name: user.fullName || '',
        email: user.email || '',
        plan,
      });
      if (redirectAfter) params.set("redirect", redirectAfter);
      window.location.href = `${CHATBOT_APP_URL}/auth/callback?${params.toString()}`;
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Invalid 2FA code");
      setSubmitting2FA(false);
    }
  };

  const fieldSx = {
    mb: 2.5,
    "& .MuiOutlinedInput-root": {
      borderRadius: 2,
      bgcolor: "#FAFAFA",
      transition: "all 0.2s",
      "&:hover": { bgcolor: "#F3F4F6" },
      "&.Mui-focused": { bgcolor: "#fff", boxShadow: "0 0 0 3px rgba(79,70,229,0.08)" },
    },
    "& .MuiInputLabel-root": { fontWeight: 500, color: "#6B7280", "&.Mui-focused": { color: "#4F46E5" } },
    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#E5E7EB", transition: "border-color 0.2s" },
    "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "#4F46E5" },
  };

  const handleResend = async () => {
    if (!needsVerification?.email) return;
    setResending(true);
    setResendDone(false);
    try {
      await resendVerification(needsVerification.email);
      setResendDone(true);
    } catch (msg) {
      setError(typeof msg === "string" ? msg : msg?.message || "Failed to resend");
    }
    setResending(false);
  };

  if (requires2FA) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "#F9FAFB" }}>
        <Box component="form" onSubmit={handle2FASubmit} sx={{ width: 360, p: 4, bgcolor: "#fff", borderRadius: 3, boxShadow: "0 8px 32px rgba(0,0,0,0.08)", textAlign: "center" }}>
          <Box sx={{ width: 56, height: 56, borderRadius: "16px", background: "linear-gradient(135deg, #6366f1, #8b5cf6)", display: "flex", alignItems: "center", justifyContent: "center", mx: "auto", mb: 2 }}>
            <SecurityIcon sx={{ color: "#fff", fontSize: 28 }} />
          </Box>
          <Typography fontWeight={800} fontSize={20} mb={0.5}>Two-Factor Authentication</Typography>
          <Typography fontSize={13} color="text.secondary" mb={3}>Enter the 6-digit code from your authenticator app</Typography>
          <TextField fullWidth label="Authentication Code" value={totpToken} onChange={e => setTotpToken(e.target.value)}
            inputProps={{ maxLength: 6, style: { textAlign: "center", fontSize: 24, letterSpacing: 8, fontWeight: 700 } }}
            sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }} />
          {error && <Typography color="error" fontSize={13} mb={1}>{error}</Typography>}
          <Button type="submit" fullWidth variant="contained" disabled={totpToken.length !== 6 || submitting2FA}
            endIcon={submitting2FA ? <SpinningLogo /> : null}
            sx={{ borderRadius: 2, py: 1.2, textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg, #6366f1, #8b5cf6)", "&.Mui-disabled": { background: "linear-gradient(135deg, #6366f1, #8b5cf6)", opacity: 0.8 } }}>
            {submitting2FA ? "Verifying…" : "Verify & Login"}
          </Button>
          <Button fullWidth sx={{ mt: 1, textTransform: "none", color: "#6b7280" }} onClick={() => { setRequires2FA(false); setTotpToken(""); setError(""); }}>
            Back to Login
          </Button>
        </Box>
      </Box>
    );
  }



  // Snackbar notification
  const snackbar = (
    <Snackbar open={snackbarOpen} autoHideDuration={4000}
      onClose={() => setSnackbarOpen(false)}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}>
      <Alert severity={snackbarSeverity} variant="filled"
        sx={{ borderRadius: 2, fontWeight: 600 }}>
        {snackbarMsg}
      </Alert>
    </Snackbar>
  );

  // Verification required popup
  const verificationDialog = (
    <Dialog open={!!needsVerification} onClose={() => setNeedsVerification(null)}
      PaperProps={{ sx: { borderRadius: 4, maxWidth: 400, width: "90%", p: 0, overflow: "visible" } }}>
      <Box sx={{
        background: "linear-gradient(135deg, #EEF2FF, #FAF5FF)", p: 4, textAlign: "center",
        borderBottom: "1px solid rgba(229,231,235,0.5)",
      }}>
        <Box sx={{
          width: 64, height: 64, borderRadius: "50%", mx: "auto", mb: 2,
          background: "linear-gradient(135deg, #6366f1, #8b5cf6)",
          display: "flex", alignItems: "center", justifyContent: "center",
          boxShadow: "0 8px 24px rgba(79,70,229,0.25)",
        }}>
          <MarkEmailReadIcon sx={{ color: "#fff", fontSize: 32 }} />
        </Box>
        <Typography fontWeight={800} fontSize={20} color="#111827" mb={0.5}>
          Email Not Verified
        </Typography>
        <Typography fontSize={13} color="#6B7280" lineHeight={1.5} mb={0.5}>
          Please verify your email address before logging in.
        </Typography>
        <Typography fontSize={12} color="#9CA3AF">
          Check your inbox{needsVerification?.email ? ` (${needsVerification.email})` : ""} and spam folder for the verification link.
        </Typography>
      </Box>
      <DialogContent sx={{ p: 3, textAlign: "center" }}>
        {resendDone ? (
          <Box>
            <Typography color="success.main" fontWeight={600} fontSize={14} mb={1}>
              ✓ Verification email sent!
            </Typography>
            <Typography fontSize={12} color="#6B7280" mb={2}>
              Please check your inbox and spam folder.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => { setNeedsVerification(null); setResendDone(false); }}
              sx={{ borderRadius: 2, textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg, #4F46E5, #7C3AED)" }}>
              OK
            </Button>
          </Box>
        ) : (
          <Box>
            <Button fullWidth variant="contained" disabled={resending} onClick={handleResend}
              endIcon={resending ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : null}
              sx={{ mb: 1.5, borderRadius: 2, py: 1.2, textTransform: "none", fontWeight: 700, background: "linear-gradient(135deg, #4F46E5, #7C3AED)" }}>
              {resending ? "Sending…" : "Resend Verification Email"}
            </Button>
            <Button fullWidth variant="text" onClick={() => { setNeedsVerification(null); }}
              sx={{ textTransform: "none", color: "#6B7280", fontWeight: 600, fontSize: 13 }}>
              Back to Login
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", bgcolor: "#F9FAFB" }}>
      {snackbar}
      {verificationDialog}
      {/* ===== LEFT: ANIMATED CONTENT ===== */}
      <Box sx={{
        flex: "0 0 55%",
        display: { xs: "none", md: "flex" },
        flexDirection: "column",
        background: "linear-gradient(135deg, #EEF2FF 0%, #FAF5FF 50%, #F0FDF4 100%)",
        position: "relative",
        overflow: "hidden",
        p: 6,
      }}>
        {/* Animated orbs */}
        <motion.div
          animate={{ x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "8%", left: "5%", width: 350, height: 350, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.08) 0%, transparent 70%)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", bottom: "10%", right: "8%", width: 400, height: 400, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)", pointerEvents: "none" }}
        />
        <motion.div
          animate={{ x: [0, 15, 0], y: [0, -15, 0] }}
          transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
          style={{ position: "absolute", top: "50%", left: "60%", width: 200, height: 200, borderRadius: "50%", background: "radial-gradient(circle, rgba(16,185,129,0.05) 0%, transparent 70%)", pointerEvents: "none" }}
        />

        {/* Content */}
        <Box sx={{ position: "relative", zIndex: 1, flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxWidth: 540, mx: "auto" }}>
          {/* Brand badge */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2, duration: 0.6 }}
          >
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1, px: 2, py: 0.6, borderRadius: "20px", bgcolor: "rgba(79,70,229,0.08)", border: "1px solid rgba(79,70,229,0.15)", mb: 3 }}>
              <Box component="img" src={logoImg} alt="Talktokam" sx={{ height: 18, width: "auto", borderRadius: "4px" }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#4F46E5", textTransform: "uppercase", letterSpacing: "0.5px" }}>
                Talktokam Platform
              </Typography>
            </Box>
          </motion.div>

          {/* Headline */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
          >
            <Typography sx={{ fontSize: { md: 34, lg: 40 }, fontWeight: 800, lineHeight: 1.15, color: "#111827", mb: 2, letterSpacing: "-1px" }}>
              Build Smarter{" "}
              <Box component="span" sx={{ background: "linear-gradient(135deg, #4F46E5, #7C3AED)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                Chatbots
              </Box>{" "}
              in Minutes
            </Typography>
          </motion.div>

          {/* Description */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <Typography sx={{ fontSize: 14, color: "#6B7280", lineHeight: 1.7, mb: 3 }}>
              Create intelligent chatbots, automate conversations, and deliver instant support — all from one dashboard. No coding required.
            </Typography>
          </motion.div>

          {/* Feature bullets */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5, duration: 0.6 }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 4 }}>
              {contentFeatures.map((f, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.6 + i * 0.1, duration: 0.4 }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                    <Box sx={{ width: 30, height: 30, borderRadius: "8px", bgcolor: "rgba(79,70,229,0.1)", display: "flex", alignItems: "center", justifyContent: "center", color: "#4F46E5", fontSize: 15 }}>
                      {f.icon}
                    </Box>
                    <Typography sx={{ fontSize: 13, fontWeight: 500, color: "#374151" }}>{f.text}</Typography>
                  </Box>
                </motion.div>
              ))}
            </Box>
          </motion.div>

          {/* Image showcase */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.6 }}
          >
            <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
              <Box
                component="img"
                src={chatgptAiImg}
                alt="AI Chatbot"
                sx={{ width: "48%", borderRadius: 2.5, boxShadow: "0 8px 30px rgba(0,0,0,0.08)", border: "1px solid rgba(229,231,235,0.4)", animation: "float 4s ease-in-out infinite" }}
              />
              <Box
                component="img"
                src={getStartedImg}
                alt="Get Started"
                sx={{ width: "48%", borderRadius: 2.5, boxShadow: "0 8px 30px rgba(0,0,0,0.08)", border: "1px solid rgba(229,231,235,0.4)", animation: "float 4s ease-in-out infinite", animationDelay: "2s" }}
              />
            </Box>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2, duration: 0.6 }}
          >
            <Box sx={{ display: "flex", gap: 4, mt: 3, pt: 3, borderTop: "1px solid rgba(229,231,235,0.5)" }}>
              {[
                { value: "5K+", label: "Bots Created" },
                { value: "500K+", label: "Conversations" },
                { value: "99.9%", label: "Uptime" },
                { value: "4.8★", label: "Rating" },
              ].map((s) => (
                <Box key={s.label} sx={{ textAlign: "center" }}>
                  <Typography sx={{ fontSize: 18, fontWeight: 800, color: "#4F46E5" }}>{s.value}</Typography>
                  <Typography sx={{ fontSize: 10, color: "#9CA3AF", fontWeight: 500 }}>{s.label}</Typography>
                </Box>
              ))}
            </Box>
          </motion.div>
        </Box>
      </Box>

      {/* ===== RIGHT: FORM ===== */}
      <Box sx={{
        flex: { xs: 1, md: "0 0 45%" },
        display: "flex", alignItems: "center", justifyContent: "center",
        p: { xs: 1.5, md: 2 },
        position: "relative",
        overflow: "hidden",
      }}>
        {/* Background decoration */}
        <Box sx={{ position: "absolute", top: "-20%", left: "-10%", width: 300, height: 300, borderRadius: "50%", background: "radial-gradient(circle, rgba(79,70,229,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />
        <Box sx={{ position: "absolute", bottom: "-10%", right: "-10%", width: 250, height: 250, borderRadius: "50%", background: "radial-gradient(circle, rgba(124,58,237,0.06) 0%, transparent 70%)", pointerEvents: "none" }} />

        <motion.div
          initial={{ opacity: 0, x: 40 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          style={{ width: "100%", maxWidth: 420 }}
        >
          <Paper elevation={0} sx={{
            p: 2, borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.85)",
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
            border: "1px solid rgba(229,231,235,0.5)",
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
          }}>
            {/* Logo + Welcome */}
            <Box sx={{ textAlign: "center", mb: 1.5 }}>
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ type: "spring", stiffness: 200, delay: 0.1 }}
              >
                <Box sx={{
                  width: 36, height: 36, borderRadius: "10px",
                  background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  boxShadow: "0 8px 24px rgba(79,70,229,0.3)", mx: "auto", mb: 1,
                }}>
                  <SmartToyIcon sx={{ color: "#fff", fontSize: 24 }} />
                </Box>
              </motion.div>
              <Typography sx={{ fontWeight: 700, fontSize: 16, color: "#111827", lineHeight: 1.2 }}>
                Welcome Back
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6B7280", mt: 0.3 }}>
                Please login to continue accessing your dashboard.
              </Typography>
            </Box>

            {/* Tabs */}
            <Box sx={{ display: "flex", bgcolor: "#F3F4F6", borderRadius: 2, p: 0.3, mb: 2.5 }}>
              {["Log In", "Sign Up"].map((tab, i) => {
                const active = (i === 0) === isLogin;
                return (
                  <Box
                    key={tab}
                    onClick={() => { if ((i === 0) !== isLogin) toggleMode(); }}
                    sx={{
                      flex: 1, textAlign: "center", py: 0.8, borderRadius: 1.5,
                      cursor: "pointer", fontWeight: 700, fontSize: 13,
                      bgcolor: active ? "#fff" : "transparent",
                      color: active ? "#4F46E5" : "#6B7280",
                      boxShadow: active ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                      transition: "all 0.2s",
                    }}
                  >
                    {tab}
                  </Box>
                );
              })}
            </Box>

            <AnimatePresence mode="wait">
              <motion.form
                key={isLogin ? "login" : "signup"}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.25 }}
                onSubmit={handleSubmit}
              >
                {!isLogin && (
                  <TextField
                    fullWidth margin="none" size="small"
                    label="Full Name"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    sx={fieldSx}
                  />
                )}

                <TextField
                  fullWidth margin="none" size="small"
                  label="Company Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  sx={fieldSx}
                />

                <TextField
                  fullWidth margin="none" size="small"
                  label="Password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  sx={fieldSx}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} size="small">
                          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                {isLogin && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2.5 }}>
                    <FormControlLabel
                      control={<Checkbox size="small" sx={{ "&.Mui-checked": { color: "#4F46E5" } }} />}
                      label={<Typography sx={{ fontSize: 12, color: "#6B7280", fontWeight: 500 }}>Remember me</Typography>}
                    />
                    <Typography
                      component="a"
                      href="#"
                      sx={{ fontSize: 12, color: "#4F46E5", fontWeight: 600, textDecoration: "none", cursor: "pointer", "&:hover": { textDecoration: "underline" } }}
                    >
                      Forgot Password?
                    </Typography>
                  </Box>
                )}

                {!isLogin && (
                  <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 2.5 }}>
                      {["8+ Char", "1 Lower", "1 Upper", "1 Special", "1 Num"].map((item) => (
                        <Box key={item} sx={{ fontSize: "10px", bgcolor: "#F3F4F6", color: "#6B7280", px: 1, py: 0.3, borderRadius: "4px", fontWeight: 500 }}>
                          {item}
                        </Box>
                      ))}
                    </Box>

                    <TextField
                      fullWidth margin="none" size="small"
                      label="Website URL"
                      name="website"
                      value={formData.website}
                      onChange={handleChange}
                      required
                      sx={fieldSx}
                    />

                    <Box sx={{ mb: 2.5 }}>
                      <Typography sx={{ fontSize: 12, fontWeight: 500, color: "#6B7280", mb: 0.5 }}>Phone Number</Typography>
                      <PhoneInput
                        international
                        defaultCountry="IN"
                        value={formData.phone}
                        onChange={(val) => setFormData(prev => ({ ...prev, phone: val || "" }))}
                        className="custom-phone-input"
                      />
                    </Box>

                    <FormControlLabel
                      control={<Checkbox name="termsAgreed" checked={formData.termsAgreed} onChange={handleChange} size="small" sx={{ "&.Mui-checked": { color: "#4F46E5" } }} />}
                      label={<Typography variant="body2" sx={{ fontSize: 11, color: "#6B7280" }}>
                        I agree to <a href="/terms" target="_blank" rel="noreferrer" style={{ color: "#4F46E5", fontWeight: 600 }}>Terms</a> & <a href="/privacy" target="_blank" rel="noreferrer" style={{ color: "#4F46E5", fontWeight: 600 }}>Privacy</a>
                      </Typography>}
                      sx={{ mb: 2.5 }}
                    />
                  </>
                )}

                

                <Button
                  type="submit"
                  variant="contained"
                  fullWidth
                  disabled={submitting}
                  endIcon={submitting ? <SpinningLogo /> : <ArrowForwardIcon />}
                  sx={{
                    mt: 2.5, py: 1.2, borderRadius: 2, textTransform: "none", fontWeight: 700, fontSize: 14,
                    background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                    boxShadow: "0 4px 16px rgba(79,70,229,0.3)",
                    "&:hover": { boxShadow: "0 8px 24px rgba(79,70,229,0.4)" },
                    "&.Mui-disabled": { background: "linear-gradient(135deg, #4F46E5, #7C3AED)", opacity: 1, color: "#fff" },
                  }}
                >
                  {submitting ? (isLogin ? "Logging in…" : "Creating Account…") : (isLogin ? "Login to Dashboard" : "Create Free Account")}
                </Button>

                <Typography align="center" mt={2.5} sx={{ fontSize: 12, color: "#6B7280" }}>
                  {isLogin ? (
                    <>Don't have an account?{" "}
                      <Button variant="text" onClick={toggleMode} sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: "#4F46E5", p: 0, minWidth: "auto" }}>
                        Sign Up
                      </Button>
                    </>
                  ) : (
                    <>Already have an account?{" "}
                      <Button variant="text" onClick={toggleMode} sx={{ textTransform: "none", fontWeight: 700, fontSize: 12, color: "#4F46E5", p: 0, minWidth: "auto" }}>
                        Log In
                      </Button>
                    </>
                  )}
                </Typography>
              </motion.form>
            </AnimatePresence>
          </Paper>
        </motion.div>
      </Box>
    </Box>
  );
};

export default AuthForm;
